import React, { useState, useEffect } from "react";
import s from "./CurrencyDropdown.module.css";
import classnames from "classnames";
import selectArrow from "../../../assets/images/common/select_arrow.svg";

type CurrencyType = {
    code: string;
    name: string;
    icon?: string | null;
    display_digits?: number;
    total_digits?: number;
};

type PropsType = {
    data: CurrencyType[];
    value?: string;
    placeholder?: string;
    id: string;
    onChange?: (selectedCurrency: CurrencyType) => void;
    disabled?: boolean;
    isError?: boolean;
    touched?: boolean;
};

const CurrencyDropdown = (props: PropsType) => {
    const { data, value, onChange, disabled, placeholder } = props;
    const [isDropdownVisible, setIsDropdownVisible] = useState(false);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyType | null>(null);

    // Update selected currency when value prop changes
    useEffect(() => {
        if (value && data.length > 0) {
            const currency = data.find(c => c.code === value);
            if (currency) {
                setSelectedCurrency(currency);
            }
        }
    }, [value, data]);

    const handleSelectCurrency = (currency: CurrencyType) => {
        setSelectedCurrency(currency);
        if (onChange) onChange(currency);
        setIsDropdownVisible(false);
    };

    useEffect(() => {
        if (isDropdownVisible) {
            const closeDropdown = (e: any) => {
                if (!e.target.closest(`#${props.id}`)) {
                    setIsDropdownVisible(false);
                }
            };
            document.addEventListener("click", closeDropdown);
            return () => document.removeEventListener("click", closeDropdown);
        }
    }, [isDropdownVisible, props.id]);

    return (
        <div className={s.currencyDropdownWrapper} id={props.id}>
            <div
                className={classnames(
                    s.currencySelect,
                    {
                        [s.disableSelect]: disabled,
                        [s.errorSelect]: props.isError && props.touched
                    }
                )}
                onClick={() => !disabled && setIsDropdownVisible(!isDropdownVisible)}
            >
                <div className={s.selectedBlock}>
                    {selectedCurrency ? (
                        <div className={s.selectedCurrency}>
                            <div className={s.currencyInfo}>
                                {selectedCurrency.icon && (
                                    <img
                                        src={selectedCurrency.icon}
                                        alt={`${selectedCurrency.code} flag`}
                                        className={s.currencyIcon}
                                        onError={(e) => {
                                            // Hide icon if it fails to load
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                )}
                                <span className={s.currencyCode}>
                                    {selectedCurrency.code}
                                </span>
                            </div>
                            <span className={s.currencyName}>
                                {selectedCurrency.name.length > 15
                                    ? `${selectedCurrency.name.slice(0, 15)}...`
                                    : selectedCurrency.name}
                            </span>
                        </div>
                    ) : (
                        <span className={s.placeholder}>
                            {placeholder || "Select currency"}
                        </span>
                    )}
                </div>
                <img
                    className={isDropdownVisible ? s.arrowReverse : s.arrow}
                    width={9}
                    height={5}
                    src={selectArrow}
                    alt=""
                />
            </div>

            {isDropdownVisible && (
                <div className={s.optionsWrapper}>
                    {data.map((currency, index) => (
                        <div
                            key={index}
                            className={s.option}
                            onClick={() => handleSelectCurrency(currency)}
                        >
                            <div className={s.optionContent}>
                                {currency.icon && (
                                    <img
                                        src={currency.icon}
                                        alt={`${currency.code} flag`}
                                        className={s.currencyIcon}
                                        onError={(e) => {
                                            // Hide icon if it fails to load
                                            e.currentTarget.style.display = 'none';
                                        }}
                                    />
                                )}
                                <div className={s.optionText}>
                                    <span className={s.currencyCode}>{currency.code}</span>
                                    <span className={s.currencyName}>{currency.name}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CurrencyDropdown;
